import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { Check, CreditCard, Sparkles, TriangleAlert, Flame } from "lucide-react";
import { PageHeader } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { useAuth } from "@/lib/auth";
import ReferAcademyModal from "@/components/ReferAcademyModal";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const PLAN_ORDER = ["tatame", "academy", "federation"];
const PLANS = [
  { key: "tatame", label: "Tatame", cap: "Up to 80 students", priceMonthly: 59, priceAnnual: 49 },
  { key: "academy", label: "Academy", cap: "Up to 300 students", priceMonthly: 119, priceAnnual: 99 },
  { key: "federation", label: "Federation", cap: "Unlimited students", priceMonthly: 299, priceAnnual: 249 },
];

function daysLeft(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
}

function StatusBanner({ status }) {
  if (status.comped) {
    return (
      <div className="card p-5 flex items-center gap-3" data-testid="saas-comped-banner"
           style={{ background: "var(--green-bg)", borderColor: "var(--green-border)" }}>
        <Check size={18} style={{ color: "var(--green)" }} />
        <div>
          <div className="font-semibold text-[14px]">Comped academy</div>
          <div className="label-tiny mt-0.5">Grandfathered on the RollOSS platform — no subscription needed.</div>
        </div>
      </div>
    );
  }
  if (status.locked) {
    return (
      <div className="card p-5 flex items-center gap-3" data-testid="saas-locked-banner"
           style={{ background: "rgba(255,80,80,0.08)", borderColor: "rgba(255,80,80,0.3)" }}>
        <TriangleAlert size={18} style={{ color: "rgb(255,120,120)" }} />
        <div>
          <div className="font-semibold text-[14px]">Subscription needed</div>
          <div className="label-tiny mt-0.5">Your trial ended. Pick a plan below to keep adding students, classes, and announcements.</div>
        </div>
      </div>
    );
  }
  if (status.has_subscription) {
    return (
      <div className="card p-5 flex items-center gap-3" data-testid="saas-active-banner"
           style={{ background: "var(--green-bg)", borderColor: "var(--green-border)" }}>
        <Check size={18} style={{ color: "var(--green)" }} />
        <div>
          <div className="font-semibold text-[14px] capitalize" data-testid="saas-billing-status">
            {status.billing_status || "active"}{status.cancel_at_period_end ? " · cancels at period end" : ""}
          </div>
          <div className="label-tiny mt-0.5">
            {status.plan_label} plan · {status.billing_interval || "monthly"}
            {status.current_period_end ? ` · renews ${status.current_period_end.slice(0, 10)}` : ""}
          </div>
        </div>
      </div>
    );
  }
  const dleft = daysLeft(status.trial_ends_at);
  if (dleft !== null) {
    return (
      <div className="card p-5 flex items-center gap-3" data-testid="saas-trial-banner"
           style={{ background: "rgba(244,185,66,0.08)", borderColor: "rgba(244,185,66,0.3)" }}>
        <Sparkles size={18} style={{ color: "rgb(244,185,66)" }} />
        <div>
          <div className="font-semibold text-[14px]">{dleft > 0 ? `${dleft} days left in your trial` : "Trial ended"}</div>
          <div className="label-tiny mt-0.5">Pick a plan below — you won't be charged until the trial ends.</div>
        </div>
      </div>
    );
  }
  return null;
}

function UsageCard({ status }) {
  const cap = status.student_cap;
  const pct = cap ? Math.min(100, (status.student_count / cap) * 100) : 0;
  const barColor = status.over_cap ? "rgb(255,120,120)" : status.near_cap ? "rgb(244,185,66)" : "var(--green)";
  return (
    <div className="card p-5" data-testid="saas-usage-card">
      <div className="label mb-2">Students</div>
      <div className="flex items-baseline gap-2">
        <span className="text-[26px] font-bold">{status.student_count}</span>
        <span className="label-tiny">{cap ? `/ ${cap} on ${status.plan_label}` : "unlimited on Federation"}</span>
      </div>
      {cap && (
        <div className="mt-3" style={{ height: 6, borderRadius: 4, background: "var(--bg-elev)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, background: barColor }} />
        </div>
      )}
      {status.over_cap && (
        <div className="label-tiny mt-2" style={{ color: "rgb(255,120,120)" }} data-testid="saas-over-cap-warning">
          You're at your plan's student cap — upgrade to add more.
        </div>
      )}
      {!status.over_cap && status.near_cap && (
        <div className="label-tiny mt-2" style={{ color: "rgb(244,185,66)" }}>Approaching your plan's student cap.</div>
      )}
    </div>
  );
}

function CardUpdateForm({ onSaved }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/saas/setup-intent");
      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (result.error) { setError(result.error.message); return; }
      await api.post("/saas/update-card", { payment_method_id: result.setupIntent.payment_method });
      onSaved();
    } catch (err) {
      setError(formatApiError(err, "Could not update card"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="card-update-form">
      <div className="input" style={{ padding: 12 }}>
        <CardElement options={{ style: { base: { color: "#eef1f5", fontSize: "14px", "::placeholder": { color: "#8a8f98" } }, invalid: { color: "rgb(255,120,120)" } } }} />
      </div>
      {error && <div className="label-tiny" style={{ color: "rgb(255,120,120)" }} data-testid="card-update-error">{error}</div>}
      <button type="submit" disabled={busy || !stripe} className="btn btn-primary" data-testid="card-update-submit">
        {busy ? "Saving…" : "Save card"}
      </button>
    </form>
  );
}

function PlanPicker({ status, interval, onIntervalChange, busy, onCheckout, onChangePlan }) {
  return (
    <div className="card p-5 md:p-6" data-testid="saas-plan-picker">
      <div className="flex items-center justify-between mb-4">
        <div className="label">Plans</div>
        <div className="inline-flex items-center p-1 rounded-full" style={{ background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
          <button onClick={() => onIntervalChange("monthly")} className="px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase"
                  style={interval === "monthly" ? { background: "var(--green)", color: "#062012" } : { color: "var(--text-dim)" }}
                  data-testid="saas-interval-monthly">Monthly</button>
          <button onClick={() => onIntervalChange("annual")} className="px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase"
                  style={interval === "annual" ? { background: "var(--green)", color: "#062012" } : { color: "var(--text-dim)" }}
                  data-testid="saas-interval-annual">Annual</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PLANS.map(p => {
          const isCurrent = status.plan === p.key && status.has_subscription;
          const price = interval === "annual" ? p.priceAnnual : p.priceMonthly;
          const isUpgrade = status.has_subscription && PLAN_ORDER.indexOf(p.key) > PLAN_ORDER.indexOf(status.plan);
          return (
            <div key={p.key} className="card p-5" style={isCurrent ? { border: "1.5px solid var(--green)" } : {}} data-testid={`saas-plan-${p.key}`}>
              <div className="text-[16px] font-bold uppercase tracking-tight">{p.label}</div>
              <div className="label-tiny mt-1">{p.cap}</div>
              <div className="flex items-baseline gap-1 mt-3">
                <span className="text-[28px] font-bold">${price}</span>
                <span className="label-tiny">/mo</span>
              </div>
              {isCurrent ? (
                <div className="pill pill-active mt-4" data-testid={`saas-current-plan-${p.key}`}>Current plan</div>
              ) : (
                <button
                  onClick={() => (status.has_subscription ? onChangePlan(p.key) : onCheckout(p.key))}
                  disabled={busy}
                  className="btn btn-primary w-full mt-4"
                  data-testid={`saas-select-${p.key}`}
                >
                  {status.has_subscription ? (isUpgrade ? "Upgrade" : "Switch") : "Start 14-day trial"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SaasBilling() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [interval, setInterval2] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [cardSaved, setCardSaved] = useState(false);
  const [referOpen, setReferOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const isWelcome = params.get("welcome") === "1";

  const refresh = useCallback(() => api.get("/saas/status").then(r => { setStatus(r.data); setInterval2(r.data.billing_interval || "monthly"); }), []);
  const refreshInvoices = useCallback(() => api.get("/saas/invoices").then(r => setInvoices(r.data.invoices)).catch(() => {}), []);

  useEffect(() => { refresh(); refreshInvoices(); }, [refresh, refreshInvoices]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    const checkout = params.get("checkout");
    if (checkout === "success" && sessionId) {
      api.get("/saas/checkout/confirm", { params: { session_id: sessionId } })
        .then(() => { refresh(); refreshInvoices(); })
        .finally(() => setParams({}, { replace: true }));
    } else if (checkout) {
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status) return <div className="p-8 label">Loading…</div>;

  const startCheckout = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/saas/checkout", { plan, interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(formatApiError(e, "Could not start checkout"));
      setBusy(false);
    }
  };

  const changePlan = async (plan) => {
    if (!window.confirm(`Switch to the ${plan} plan (${interval})?`)) return;
    setBusy(true);
    try { await api.post("/saas/change-plan", { plan, interval }); await refresh(); }
    catch (e) { alert(formatApiError(e, "Could not change plan")); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your RollOSS subscription at the end of the current period?")) return;
    setBusy(true);
    try { await api.post("/saas/cancel"); await refresh(); }
    catch (e) { alert(formatApiError(e, "Could not cancel")); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try { await api.post("/saas/reactivate"); await refresh(); }
    catch (e) { alert(formatApiError(e, "Could not reactivate")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="page-saas-billing">
      <PageHeader section="Account" title="Subscription" />
      <div className="p-4 md:p-8 space-y-6" style={{ maxWidth: 980 }}>
        {isWelcome && (
          <div className="card p-5 flex items-center justify-between gap-3" data-testid="saas-welcome-banner"
               style={{ background: "var(--green-bg)", borderColor: "var(--green-border)" }}>
            <div>
              <div className="font-semibold text-[14px]">Welcome to RollOSS!</div>
              <div className="label-tiny mt-0.5">Pick a plan to start your free 14-day trial — no charge until it ends. Or explore the app first and come back anytime.</div>
            </div>
            <Link to="/app" className="btn btn-outline shrink-0" data-testid="saas-skip-welcome">Skip for now</Link>
          </div>
        )}
        <StatusBanner status={status} />

        <div className="card flex flex-col md:flex-row items-center justify-between gap-3 p-5 px-6" data-testid="refer-academy-card"
          style={{ background: "linear-gradient(90deg, rgba(61,220,126,0.10), rgba(61,220,126,0.02))", border: "1px solid var(--green-border)" }}>
          <div className="flex items-center gap-3">
            <Flame size={18} style={{ color: "var(--green)" }} />
            <div>
              <div className="font-semibold text-[14px]">Refer another academy</div>
              <div className="label-tiny mt-0.5">Founding academies — first 10 only get 50% off, locked in for 12 months.</div>
            </div>
          </div>
          <button onClick={() => setReferOpen(true)} className="btn btn-outline shrink-0" data-testid="btn-refer-academy-billing">Refer an academy</button>
        </div>
        {referOpen && <ReferAcademyModal academyName={user?.academy_name} onClose={() => setReferOpen(false)} />}

        {!status.comped && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <UsageCard status={status} />
              <div className="card p-5" data-testid="saas-payment-method-card">
                <div className="label mb-2">Payment method</div>
                {status.has_subscription ? (
                  <Elements stripe={stripePromise}>
                    <CardUpdateForm onSaved={() => setCardSaved(true)} />
                  </Elements>
                ) : (
                  <div className="label-tiny" style={{ color: "var(--text-dim)" }}>Start a subscription first to add a payment method.</div>
                )}
                {cardSaved && <div className="label-tiny mt-2" style={{ color: "var(--green)" }} data-testid="card-saved-confirm">Card updated.</div>}
              </div>
            </div>

            <PlanPicker status={status} interval={interval} onIntervalChange={setInterval2} busy={busy}
                        onCheckout={startCheckout} onChangePlan={changePlan} />

            {status.has_subscription && (
              <div className="card p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="label mb-1">Manage subscription</div>
                  <div className="label-tiny">
                    {status.cancel_at_period_end
                      ? `Cancels on ${status.current_period_end?.slice(0, 10) || "period end"}`
                      : "Cancel anytime — access continues through the paid period."}
                  </div>
                </div>
                {status.cancel_at_period_end ? (
                  <button onClick={reactivate} disabled={busy} className="btn btn-outline" data-testid="saas-reactivate-btn">Reactivate</button>
                ) : (
                  <button onClick={cancel} disabled={busy} className="btn btn-outline"
                          style={{ borderColor: "rgba(255,80,80,0.35)", color: "rgb(255,120,120)" }}
                          data-testid="saas-cancel-btn">Cancel subscription</button>
                )}
              </div>
            )}

            <div className="card p-5 md:p-6" data-testid="saas-invoices-card">
              <div className="label mb-3">Invoices</div>
              {invoices.length === 0 ? (
                <div className="label-tiny" style={{ color: "var(--text-dim)" }} data-testid="saas-invoices-empty">No invoices yet.</div>
              ) : (
                <div className="space-y-2">
                  {invoices.map(inv => (
                    <a key={inv.invoice_id} href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                       className="flex items-center justify-between py-2 border-b hover:bg-white/5" style={{ borderColor: "var(--border)" }}
                       data-testid={`saas-invoice-${inv.invoice_id}`}>
                      <div className="flex items-center gap-2">
                        <CreditCard size={14} style={{ color: "var(--green)" }} />
                        <span className="text-[13px]">${inv.amount_paid.toFixed(2)}</span>
                      </div>
                      <span className="label-tiny capitalize">{inv.status}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
